// Which line rings on which handset.
//
// Kevin, 2026-08-10: "you also have access to be able to set which line is on which handset."
//
// A DP base holds up to ten SIP accounts and five handsets, and each handset carries its own
// subset. Until now Verso could read that and not change it, which meant the answer to "why
// didn't it ring?" lived in GDMS.
//
// THE FAILURE THIS PREVENTS IS SILENT. A line on no handset rings nothing while the caller
// hears ringback — indistinguishable from a routing fault, and it cost an afternoon on
// 2026-08-10. So a line assigned to no handset at all is called out here rather than left as
// an empty column nobody reads.
//
// TWO CHANGES ON 2026-08-17, both because this table named things the way GDMS does rather
// than the way an operator does:
//
//   • The Line column showed the raw SIP username (`13038232345`). That is the same
//     complaint Kevin made about the call tables — "the more operator friendly line number
//     and subscriber name" — so it now reads the number, the line label and the customer,
//     resolved server-side by GET /devices/:id/handsets.
//   • RING MODE IS SHOWN. The write below forces Parallel, and the old comment here said so
//     as though that settled it. It does not: a base configured by hand in GDMS keeps
//     whatever GDMS defaulted to, and GDMS defaults to Circular — which rings a line's
//     handsets one at a time, so every other call rings a handset nobody is holding. Forcing
//     it on write protects bases we have written; it says nothing about the rest of the
//     fleet (feedback_shipped_is_not_effective). Now the page says which mode is really set.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { fmtPhone } from '../../lib/calls.js';

const HANDSETS = [1, 2, 3, 4, 5];

/** The line as an operator says it: the number, then who it belongs to. */
function LineName({ line }) {
  const number = line.number ? fmtPhone(line.number) || line.number : null;
  return (
    <>
      <span className="font-medium text-gray-900">{number || line.extension || `slot ${line.slot}`}</span>
      {(line.label || line.subscriberName) && (
        <span className="ml-2 text-xs text-gray-500">
          {line.subscriberId ? (
            <Link to={`/subscribers/${line.subscriberId}`} className="hover:underline">
              {line.label || line.subscriberName}
            </Link>
          ) : (line.label || line.subscriberName)}
        </span>
      )}
      {/* GDMS holds an account here that Verso has no binding for. Worth saying on the row
          rather than in a summary: it means the number beside it came from GDMS alone. */}
      {line.unmatched && (
        <span className="ml-2 rounded-md border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-600">
          not linked in Verso
        </span>
      )}
    </>
  );
}

export default function HandsetMatrix({ device, canEdit }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);   // { [handset]: Set(accountIndex) }

  const q = useQuery({
    queryKey: ['device', device.id, 'handsets'],
    queryFn: () => api.get(`/devices/${device.id}/handsets`),
  });

  const save = useMutation({
    mutationFn: (matrix) => api.put(`/devices/${device.id}/handsets`, { matrix }),
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['device', device.id, 'handsets'] });
      qc.invalidateQueries({ queryKey: ['device', device.id, 'gdms-live'] });
      qc.invalidateQueries({ queryKey: ['line'] });
    },
  });

  const slots = q.data?.slots || [];
  const rows  = q.data?.handsets || [];
  const ring  = q.data?.ring || [];

  // What GDMS holds, as handset -> Set(slot). The endpoint has already dropped the zeros
  // out of `line1..line10`, so there is no fixed-width array to unpick here any more.
  const live = {};
  for (const n of HANDSETS) {
    live[n] = new Set((rows.find((r) => Number(r.handset) === n)?.slots) || []);
  }

  const current = draft || live;
  const matrixChanged = draft != null && HANDSETS.some(
    (n) => [...current[n]].sort().join() !== [...live[n]].sort().join(),
  );

  const toggle = (handset, slot) => {
    const next = {};
    for (const n of HANDSETS) next[n] = new Set(current[n]);
    if (next[handset].has(slot)) next[handset].delete(slot);
    else next[handset].add(slot);
    setDraft(next);
  };

  const submit = () => save.mutate(HANDSETS.map((n) => ({
    handset: n,
    slots: [...current[n]].sort((a, b) => a - b),
  })));

  // A line nobody can answer. The whole reason this component exists.
  const orphans = slots.filter((s) => !HANDSETS.some((n) => current[n].has(s.slot)));

  // A line on more than one handset, set to a mode that rings them in turn. This is the
  // 2026-08-10 fault, and it is invisible in the matrix itself — the ticks look right.
  // TWO DIFFERENT FAULTS, AND THEY FEEL DIFFERENT TO THE CUSTOMER.
  //
  // Kevin, 2026-08-20, on Conrad Carpenter's DP750: "GDMS shows HS mode is HS1. Not sure what that
  // translates to though." It translates to hsMode 1 — "handset 1 only" — and the warning here
  // described Circular's symptom for it: "every other call rings a handset nobody is holding."
  // Wrong, and misleading in the direction that matters. With HS1 there is nothing intermittent:
  // handset 1 always rings and the other four never do, so somebody carrying handset 3 hears
  // nothing, ever.
  //
  // `single` is the contradiction — a mode naming ONE handset while the line is on several — and it
  // is what saving corrects. `hunting` is Circular or Linear, a real hunt group, which saving
  // deliberately leaves alone.
  const onSeveral = (slot) => HANDSETS.filter((n) => current[n].has(slot)).length > 1;
  const single = ring.filter((r) => !r.parallel && Number(r.mode) >= 1 && Number(r.mode) <= 5
    && onSeveral(r.slot));
  const hunting = ring.filter((r) => !r.parallel && Number(r.mode) > 100 && onSeveral(r.slot));

  // THERE IS SOMETHING TO SAVE EVEN WHEN NOTHING WAS TICKED.
  //
  // `dirty` was "the matrix differs from what GDMS holds", which is right for the ticks and wrong
  // for the fault: Conrad Carpenter's DP750 had the correct ticks and the wrong ring mode, so the
  // card told an operator to press a button that was disabled (2026-08-20). The one action that
  // fixes it was unreachable from the only screen that names it.
  const dirty = matrixChanged || single.length > 0;

  const label = (slot) => {
    const s = slots.find((x) => x.slot === slot);
    return s ? (s.number ? (fmtPhone(s.number) || s.number) : (s.extension || `slot ${slot}`)) : `slot ${slot}`;
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Handsets</h2>
          <p className="mt-1 text-sm text-gray-500">
            Which line rings on which handset. A line ticked for several handsets rings them
            all at once.
          </p>
        </div>
      </div>

      {q.isLoading && <p className="mt-3 text-sm text-gray-500">Reading the handsets from GDMS…</p>}
      {q.error && (
        <p className="mt-3 text-sm text-red-700">
          Could not read the handsets: {q.error.body?.error || q.error.message}
        </p>
      )}

      {/* COULD NOT ASK IS NOT THE SAME AS NOTHING ASSIGNED. An offline base, an
          unconfigured GDMS and a base with no lines on it used to render identically as an
          empty table, which reads as "this base is set up and rings nothing". */}
      {!q.isLoading && !q.error && q.data && q.data.available === false && (
        <p className="mt-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          The handsets could not be read, so what is below is unknown rather than empty:{' '}
          {q.data.reason}
        </p>
      )}

      {!q.isLoading && !q.error && q.data?.available && !slots.length && (
        <p className="mt-3 text-sm text-gray-500">
          This base has no lines on it yet, so there is nothing to put on a handset.
        </p>
      )}

      {!q.isLoading && !q.error && slots.length > 0 && (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">Line</th>
                  {HANDSETS.map((n) => (
                    <th key={n} className="px-3 py-2 text-center">HS{n}</th>
                  ))}
                  <th className="px-3 py-2 text-left">Rings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {slots.map((s) => {
                  const on = HANDSETS.filter((n) => current[n].has(s.slot));
                  const mode = ring.find((r) => r.slot === s.slot);
                  return (
                    <tr key={s.slot}>
                      <td className="py-2.5 pr-4">
                        <LineName line={s} />
                        {!on.length && (
                          <span className="ml-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
                            rings no handset
                          </span>
                        )}
                      </td>
                      {HANDSETS.map((n) => (
                        <td key={n} className="px-3 py-2.5 text-center">
                          <input
                            type="checkbox"
                            aria-label={`${label(s.slot)} on handset ${n}`}
                            checked={current[n].has(s.slot)}
                            disabled={!canEdit || save.isPending}
                            onChange={() => toggle(n, s.slot)}
                          />
                        </td>
                      ))}
                      {/* Only interesting when it is NOT Parallel, and only actionable when
                          the line is on more than one handset. Said quietly otherwise. */}
                      <td className="px-3 py-2.5 text-left text-xs">
                        {mode == null ? (
                          <span className="text-gray-400">—</span>
                        ) : mode.parallel ? (
                          <span className="text-gray-500">all at once</span>
                        ) : (
                          <span className="font-medium text-amber-900">{mode.label}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {orphans.length > 0 && (
            <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {orphans.length === 1 ? 'This line rings' : 'These lines ring'} no handset:{' '}
              {orphans.map((s) => label(s.slot)).join(', ')}. Calls to{' '}
              {orphans.length === 1 ? 'it' : 'them'} give the caller ringback and nothing here
              will ring.
            </p>
          )}

          {single.length > 0 && (
            <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {single.length === 1 ? 'This line is' : 'These lines are'} ticked for several
              handsets but set to ring <strong>only one of them</strong>:{' '}
              {single.map((r) => `${label(r.slot)} (${r.label})`).join(', ')}. The other handsets
              never ring at all, so anyone carrying one hears nothing.{' '}
              <strong>Saving here sets them to ring together.</strong>
            </p>
          )}

          {hunting.length > 0 && (
            <p className="mt-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              {hunting.length === 1 ? 'This line rings' : 'These lines ring'} its handsets{' '}
              <strong>one at a time</strong>:{' '}
              {hunting.map((r) => `${label(r.slot)} (${r.label})`).join(', ')} — a hunt group, so a
              given call rings one handset and moves on. Left as it is; saving does not change it.
            </p>
          )}

          {save.isError && (
            <p className="mt-3 text-sm text-red-700">{save.error.body?.error || save.error.message}</p>
          )}
          {save.isSuccess && !save.data?.handsets && (
            // Sent, but we could not read the base back — that is not the same as applied.
            <p className="mt-3 text-sm text-amber-800">
              GDMS accepted the change, but reading the handsets back failed, so this has not
              been confirmed on the base.
            </p>
          )}

          {canEdit && (
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                disabled={!dirty || save.isPending}
                onClick={submit}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {save.isPending ? 'Saving…' : 'Save handsets'}
              </button>
              {dirty && (
                <button
                  type="button"
                  onClick={() => { setDraft(null); save.reset(); }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
              <span className="text-xs text-gray-500">Saved straight to GDMS.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
